import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { FileText, Loader2, CheckCircle, Plus, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { DocumentUpload } from "@/components/DocumentUpload";
import type { ParsedJob, MetricQuestion } from "@/pages/ResumeQuantification";

interface QuantificationUploadStepProps {
  onComplete: (parsedJobs: ParsedJob[], questions: Record<string, MetricQuestion[]>, firstName: string, lastName: string) => void;
}

export function QuantificationUploadStep({ onComplete }: QuantificationUploadStepProps) {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState("");
  const [pastedResumeText, setPastedResumeText] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const processResume = async (resumeContent: string) => {
    if (!firstName.trim() || !lastName.trim()) {
      toast({
        title: "Name required",
        description: "Please enter your first and last name before processing.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    setIsProcessing(true);
    setProcessingProgress(0);
    setProcessingStage("Parsing your resume...");

    try {
      // Simulate initial progress
      const progressInterval = setInterval(() => {
        setProcessingProgress(prev => {
          if (prev < 40) return prev + 5;
          if (prev < 70) return prev + 2;
          if (prev < 85) return prev + 1;
          return prev;
        });
      }, 500);

      setProcessingStage("Extracting professional experience...");
      await new Promise(resolve => setTimeout(resolve, 1000));

      setProcessingStage("Generating Metrics Questions for Your Resume...");
      
      const { data, error } = await supabase.functions.invoke('ai-resume-quantification-pipeline', {
        body: { resumeContent, additionalNotes }
      });

      clearInterval(progressInterval);

      if (error) throw new Error(error.message);
      if (!data.parsedJobs || data.parsedJobs.length === 0) {
        throw new Error("No professional experience found in the resume");
      }

      setProcessingProgress(100);
      setProcessingStage("Complete!");

      toast({
        title: "Resume processed successfully",
        description: `Found ${data.parsedJobs.length} position${data.parsedJobs.length > 1 ? 's' : ''} with questions generated`,
      });

      setTimeout(() => {
        onComplete(data.parsedJobs, data.questions, firstName, lastName);
      }, 500);

    } catch (error) {
      console.error('Processing error:', error);
      toast({
        title: "Processing failed",
        description: error instanceof Error ? error.message : "Failed to process resume",
        variant: "destructive",
      });
      setProcessingProgress(0);
      setProcessingStage("");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = async (text: string) => {
    if (!text || text.trim().length < 100) {
      toast({
        title: "Invalid resume",
        description: "The resume content appears to be too short or empty.",
        variant: "destructive",
      });
      return;
    }
    await processResume(text);
  };

  const handlePasteSubmit = async () => {
    if (!pastedResumeText.trim() || pastedResumeText.trim().length < 100) {
      toast({
        title: "Text too short",
        description: "Please provide a complete resume with professional experience.",
        variant: "destructive",
      });
      return;
    }
    await processResume(pastedResumeText);
  };

  if (isProcessing) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-6" />
        <h2 className="text-xl font-semibold mb-4">{processingStage}</h2>
        <div className="max-w-md mx-auto">
          <Progress value={processingProgress} className="h-3 mb-2" />
          <p className="text-sm text-muted-foreground">{Math.round(processingProgress)}%</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <FileText className="h-12 w-12 text-primary mx-auto mb-4" />
        <h2 className="text-2xl font-semibold mb-2">Upload Your Resume</h2>
        <p className="text-muted-foreground">
          We'll extract your experience and generate questions to help add meaningful metrics
        </p>
      </div>

      {/* Name Fields */}
      <Card className="p-4 bg-primary/5 border-primary/20">
        <div className="flex items-center gap-2 mb-4">
          <User className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Your Name (Required)</span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              placeholder="John"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              placeholder="Smith"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>
      </Card>

      <Tabs defaultValue="upload" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="upload">Upload File</TabsTrigger>
          <TabsTrigger value="paste">Paste Text</TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="space-y-6 mt-6">
          <DocumentUpload
            onUploadComplete={handleFileUpload}
            title="Upload Resume"
            description="PDF, DOC, DOCX, or TXT format"
            acceptedTypes={['.pdf', '.doc', '.docx', '.txt']}
          />
        </TabsContent>

        <TabsContent value="paste" className="space-y-6 mt-6">
          <div className="space-y-4">
            <Textarea
              placeholder="Paste your complete resume text here..."
              value={pastedResumeText}
              onChange={(e) => setPastedResumeText(e.target.value)}
              className="min-h-[250px] resize-none"
            />
            <Button
              onClick={handlePasteSubmit}
              className="w-full"
              disabled={!pastedResumeText.trim()}
            >
              Process Resume
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Additional Notes Section */}
      <Card className="p-4 bg-muted/30">
        <div className="flex items-center gap-2 mb-3">
          <Plus className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Additional Context (Optional)</span>
        </div>
        <Textarea
          placeholder="Add any additional notes, documents, or context that might help generate better questions..."
          value={additionalNotes}
          onChange={(e) => setAdditionalNotes(e.target.value)}
          className="min-h-[80px] resize-none"
        />
      </Card>
    </div>
  );
}
